import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { kitService } from "/app/src/services";

import { Search } from "/app/src/components/generic/components/search";

import { useTranslation } from "react-i18next";
import { Kit as KitType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Kits component renders the kits page with search functionality, paginated data, and table display.
 *
 * @returns {JSX.Element} The rendered kits component.
 */
export default function Kits() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const columnUpgrade = createColumnHelper<KitType>();
  const columns = useMemo(
    () => [
      columnUpgrade.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: t("translation:name"),
      }),
    ],
    [t, columnUpgrade],
  );

  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const goToKit = useCallback(
    (kitId: number) => {
      navigate(`/explorer/kits/${kitId}`);
    },
    [navigate],
  );

  const {
    data: kits,
    count: kitsCount,
    isFetching,
  } = usePaginatedData<KitType>({
    queryKey: ["kits"],
    searchString,
    sort,
    page,
    pageSize,
    service: kitService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: kitsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:kits")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={kits}
        tableColumns={columns}
        length={kitsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToKit}
        emptyText={t("translation:no_kits_found")}
      />
    </div>
  );
}
